import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Service } from '@app/services/_models/service.model';
import { TechnicalPoint } from '@app/tech-points/_models/technical-point.model';
import { City } from '@models/city.model';
import { Status } from '@models/status.model';
import { NomenclatureService } from '@services/nomenclature.service';
import { StatusCode } from '@app/_enums/status-code';

import { EMPTY, forkJoin, Observable, of, OperatorFunction, Subscription } from 'rxjs';
import { catchError, concatMap, debounceTime, first, repeatWhen, switchMap, tap } from 'rxjs/operators';

import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { SearchTechnicalPoint } from '@app/tech-points/_models/search-technical-point.mode';
import { VoucherService } from '@app/vouchers/_services/voucher.service';
import { ListVoucher } from '@app/vouchers/_models/list-vouchers.model';
import { TranslateService } from '@ngx-translate/core';
import { ExportService } from '@services/export.service';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { displayError, displayErrorFromUnknown } from '@app/_utils/error-util';
import { convertDateToString } from '@app/_utils/date-util';

@Component({
  selector: 'app-list-promotions',
  templateUrl: './list-vouchers.component.html',
  styleUrls: ['./list-vouchers.component.css']
})
export class ListVouchersComponent extends BaseSortableSearchComponent<ListVoucher> implements OnInit, OnDestroy {
  // Units
  cities:           City[];
  statuses:         Status[];
  services:         Service[];
  promotionTypeId:  number;
  technicalPoints:  TechnicalPoint[];

  // Constants
  readonly MAIN_SERVICE_GTP_NAME:     string = "ГОДИШЕН ТЕХНИЧЕСКИ ПРЕГЛЕД";

  // Booleans
  loadPdfOrExcel = false;

  // Form
  searchForm = this.formBuilder.group({
    name:             [null],
    status:           [null],
    service:          [null]
  });

  // Observables
  searchSubscription: Subscription;
  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() => {
      return this.voucherService.pageable(this.sortingPaging, this.searchForm.value).pipe(
        tap(([page, totalCount]) => {
          this.sortingPaging.fromRow = page.fromRow;
          this.sortingPaging.toRow = page.toRow;
          this.sortingPaging.totalElements = totalCount;
          this.content = page.content;
          this.dataLoaded = true;
        })
      )
    }),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      this.errorMessageSubject.next(this.translateService.instant("messages.errorLoadingData"));
      return EMPTY;
    })
  );

  loadSearchForm$ = this.route.queryParams.pipe(
    switchMap(params => {
      return forkJoin([
        this.nomenclatureService.getCities(),
        this.nomenclatureService.findAllValidServices(),
        this.nomenclatureService.getStatusesByType(this.checkStatus())        
        ]).pipe(
          tap(([cities, services, statuses]) => {
            this.cities = cities;
            this.statuses = statuses;
            this.services = services;
            const gtp = this.services.splice(this.services.findIndex((item: { name: string; }) => item.name === this.MAIN_SERVICE_GTP_NAME), 1)[0];
            if (gtp) this.services.splice(0, 0, gtp);

            this.searchSubject.next();
          }),
        catchError(err => {
          displayError(err);
          this.errorMessageSubject.next(this.translateService.instant("messages.errorLoadingData"));
          return EMPTY;
        }),
        repeatWhen(() => this.reload$));
    }) 
  );

  technicalPointsSearch: OperatorFunction<string, SearchTechnicalPoint[]> = (input$: Observable<string>) =>
  input$.pipe(
    debounceTime(300),
    switchMap(input =>
      this.technicalPointService.filterTechnicalPoints(input).pipe(
        catchError(() => of([]))
      )
    )
  );

  constructor(
    private perms: PermissionsService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private exportService: ExportService, 
    private translateService: TranslateService,
    private voucherService: VoucherService,
    private nomenclatureService: NomenclatureService,
    private technicalPointService: TechnicalPointService
  ) {
    super();
  }

  ngOnInit() {
    this.searchSubscription = this.search$.subscribe();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  checkStatus() {
    return StatusCode.PromotionDiscount;
  }

  technicalPointFormatter(element: any): string {
    return element.name;
  }

  async exportExcel() {
    this.loadPdfOrExcel = true;
    const fileName = this.getFileName();

    try {
      this.exportService.exportAsExcelFile(await this.prepareData(), this.getHeadings(), this.getFilterBody(), this.getFilterHeading(), fileName);
    } catch (err) {
      displayErrorFromUnknown(err);
    } finally {
      this.loadPdfOrExcel = false;
    }
  }
 
  async exportPDF() {
    this.loadPdfOrExcel = true;
    const fileName = this.getFileName();

    try {
      this.exportService.exportAsPdfFile(await this.prepareData(), this.getHeadings(), this.getFilterBody(), this.getFilterHeading(), fileName, convertDateToString(new Date()));
    } catch (err) {
      displayErrorFromUnknown(err);
    } finally {
      this.loadPdfOrExcel = false;
    }
  }

  private getFileName() {
    return this.translateService.instant('exportData.filesNames.vouchersResult');
  }

  private async prepareData() {
    let newContent: any = [];
    let sortingPagingCopy: SortingPagingData= new SortingPagingData(this.sortingPaging.totalElements);
    sortingPagingCopy.pageSize = this.sortingPaging.totalElements;
    sortingPagingCopy.sortBy = this.sortingPaging.sortBy;
    sortingPagingCopy.sortAsc = this.sortingPaging.sortAsc;

    let result = await this.voucherService.findAllByFilter(sortingPagingCopy, this.searchForm.value).pipe(first()).toPromise();

    result.content.forEach(object => {
      // Apply the same logic as in the HTML template for price attribute display
      const priceAttributeKind = object.priceAttributeKind === 'V' ? 'Стойност' : 
                                object.priceAttributeKind === 'P' ? 'Процент' : 
                                object.priceAttributeKind;
      
      const priceAttributeAmount = object.priceAttributeAmountDds != null ? 
                                  object.priceAttributeAmountDds : 
                                  object.priceAttributeAmount;

      newContent?.push([
        object.name, 
        priceAttributeKind, 
        priceAttributeAmount, 
        object.points, 
        object.status.name, 
        object.validFrom, 
        object.validTo, 
        object.services, 
        object.technicalPoints
      ]);
    });

    return newContent;
  }
 
  private getHeadings() : string[][] {
    return [[this.translateService.instant("voucher.name"), 
             this.translateService.instant("voucher.discountKind"), 
             this.translateService.instant("voucher.discount"), 
             this.translateService.instant("voucher.points"), 
             this.translateService.instant("voucher.status"), 
             this.translateService.instant("voucher.validFrom"), 
             this.translateService.instant("voucher.validTo"), 
             this.translateService.instant("voucher.services"),
             this.translateService.instant("voucher.technicalPoints")]];
  }
   
  private getFilterBody(): any[] {
    let status: Status | null;
    let service: Service | null;
    let city: City | null;

    status = this.searchForm.get('status')?.value;
    service = this.searchForm.get('service')?.value;
    city = this.searchForm.get('city')?.value;

    let result: string[]= [
      this.searchForm.get('name')?.value,
      status?.name, 
      service?.name,
      this.searchForm.get('technicalPoint')?.value,
      city?.name
    ];

    return [result];
  }
 
  private getFilterHeading(): any[][] {
    let colSpan = 8;
    let result: any[] = [
      this.translateService.instant('exportData.listPromotions.name'),
      this.translateService.instant('exportData.listPromotions.status'),
      this.translateService.instant('exportData.listPromotions.service'),
      this.translateService.instant('exportData.listPromotions.technicalPoint'),
      this.translateService.instant('exportData.listPromotions.city')
    ];

    let content: string = this.translateService.instant('menu.searchVoucher');
 
    return [
       [{content: content, colSpan: colSpan, styles: {halign: 'center'}}],
       result
    ];
  }

  get canView() {
    return this.perms.hasAccess(this.perms.CAN_VIEW_MARKETING_MENU);
  } 

  get canAddEdit() {
    return this.perms.hasAccess(this.perms.CAN_VIEW_MARKETING_MENU);
  }

}